import { BookOpen, Compass, Languages, LibraryBig, Music, ShieldAlert, Volume1, Volume2, VolumeX } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Atmosphere from "./Atmosphere";
import { useAudio, setVolume, setMuteAll, setMuteMusic, play } from "../lib/audio";
import { useVoice } from "../lib/voice";

const FONT_SCALE_KEY = "som_fontscale_react";
const FONT_SCALE_DEFAULT_KEY = "som_fontscale_default_115";
const DEFAULT_FONT_SCALE = 115;

function readInitialFontScale() {
  try {
    const migrated = localStorage.getItem(FONT_SCALE_DEFAULT_KEY) === "1";
    if (!migrated) {
      localStorage.setItem(FONT_SCALE_DEFAULT_KEY, "1");
      return DEFAULT_FONT_SCALE;
    }

    const saved = Number(localStorage.getItem(FONT_SCALE_KEY));
    return Number.isFinite(saved) && saved >= 85 && saved <= 125
      ? saved
      : DEFAULT_FONT_SCALE;
  } catch {
    return DEFAULT_FONT_SCALE;
  }
}

const navItems = [
  { to: "/", label: "Bíblia", icon: BookOpen },
  { to: "/atlas", label: "Atlas", icon: Compass },
  { to: "/vriyl", label: "Vriyl", icon: Languages },
  { to: "/codex", label: "Códex", icon: LibraryBig },
];

export default function Shell({ children }) {
  const [fontScale] = useState(readInitialFontScale);
  const [progress, setProgress] = useState(0);
  const [navExpanded, setNavExpanded] = useState(false);
  const { muteAll, muteMusic, volume } = useAudio();
  const [soundOpen, setSoundOpen] = useState(false);
  const soundRef = useRef(null);
  const voice = useVoice();
  const { pathname } = useLocation();
  const isLanding = pathname === "/" || pathname === "/index.html";

  // Fechar o painel de som ao clicar fora / Esc.
  useEffect(() => {
    if (!soundOpen) return undefined;
    const onDown = (event) => { if (soundRef.current && !soundRef.current.contains(event.target)) setSoundOpen(false); };
    const onKey = (event) => { if (event.key === "Escape") setSoundOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
  }, [soundOpen]);

  // Som nos controlos (delegação global; no-op se estiver em mudo ou sem gesto).
  useEffect(() => {
    const CTRL = "button, a[href], [role='button'], .cta, .react-tabs button, .idx-card";
    // .voice-toggle e .vibration-cta têm som próprio ("click" via manager); não duplicar.
    const SELF = ".voice-toggle, .vibration-cta";
    // Som no PRESS (pointerdown), não no click/release — sai mais cedo e alinha com o :active.
    const onDown = (event) => {
      if (event.target.closest(SELF)) return;
      if (event.target.closest(CTRL)) play("click");
    };
    let lastHover = null;
    let lastAt = 0;
    const onOver = (event) => {
      const el = event.target.closest(CTRL + ", .voice-toggle, .sound-toggle");
      if (!el || el === lastHover) return;
      const now = event.timeStamp || Date.now();
      lastHover = el;
      if (now - lastAt < 60) return;
      lastAt = now;
      play("hover");
    };
    const onOut = (event) => {
      if (lastHover && !lastHover.contains(event.relatedTarget)) lastHover = null;
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
    };
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--reader-scale", String(fontScale / 100));
    try {
      localStorage.setItem(FONT_SCALE_KEY, String(fontScale));
      localStorage.setItem(FONT_SCALE_DEFAULT_KEY, "1");
    } catch {
      // Preferimos manter a leitura funcional mesmo quando o browser bloqueia storage.
    }
  }, [fontScale]);

  useEffect(() => {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
      setNavExpanded(isLanding && window.scrollY > Math.max(260, window.innerHeight * 0.72));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [children, isLanding]);

  return (
    <div className={`app-shell ${navExpanded ? "nav-expanded" : ""}`}>
      <Atmosphere />
      <div className="confidential-bar">
        <span><ShieldAlert size={13} /> Confidencial · Documento interno</span>
        <span>Apenas equipa de desenvolvimento</span>
        <span>Lore em consolidação</span>
      </div>
      <motion.div className="reading-progress" animate={{ scaleX: progress }} />

      <nav className="site-nav-react" aria-label="Navegação principal">
        <NavLink to="/" className={`nav-wordmark ${voice === "syrr" ? "is-syrr" : ""}`} aria-label="Song of Madness">
          <span className="nav-symbol" aria-hidden="true" style={{ transform: voice === "syrr" ? "rotate(360deg)" : "rotate(0deg)" }}>
            <img className="ns-gold" src="/brand/symbol-gold.svg" alt="" style={{ opacity: voice === "syrr" ? 0 : 1 }} />
            <img className="ns-syrr" src="/brand/symbol-syrr.svg" alt="" style={{ opacity: voice === "syrr" ? 1 : 0 }} />
          </span>
        </NavLink>
        <div className="nav-routes">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => isActive ? "active" : ""}>
              {({ isActive }) => <><Icon size={15} /><span>{label}</span>{isActive && <motion.i layoutId="nav-glow" transition={{ type: "spring", stiffness: 420, damping: 32 }} />}</>}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="content-shell">{children}</div>

      <div className={`floating-tools ${isLanding ? "with-voice" : ""}`}>
      <div className="sound-bubble" ref={soundRef}>
        <AnimatePresence>
          {soundOpen && (
            <motion.div
              className="sound-panel"
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.96 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              role="group"
              aria-label="Controlo de som"
            >
              <div className="sp-row sp-vol">
                <span className="sp-label">Volume</span>
                <input
                  type="range" min="0" max="100" step="1"
                  value={Math.round(volume * 100)}
                  onChange={(e) => setVolume(Number(e.target.value) / 100)}
                  aria-label="Volume geral"
                />
                <span className="sp-val">{Math.round(volume * 100)}</span>
              </div>
              <button type="button" className={`sp-toggle ${muteMusic ? "off" : "on"}`} onClick={() => setMuteMusic(!muteMusic)} aria-pressed={!muteMusic}>
                <Music size={14} /><span>Música</span><i>{muteMusic ? "silenciada" : "ligada"}</i>
              </button>
              <button type="button" className={`sp-toggle ${muteAll ? "off" : "on"}`} onClick={() => setMuteAll(!muteAll)} aria-pressed={!muteAll}>
                {muteAll ? <VolumeX size={14} /> : <Volume2 size={14} />}<span>Tudo</span><i>{muteAll ? "silenciado" : "ligado"}</i>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          className={`sound-toggle ${muteAll ? "" : "on"}`}
          onClick={() => setSoundOpen((v) => !v)}
          aria-expanded={soundOpen}
          aria-label="Controlo de som"
          title="Som"
        >
          {muteAll ? <VolumeX size={16} /> : muteMusic ? <Volume1 size={16} /> : <Volume2 size={16} />}
          <span>Som</span>
        </button>
      </div>

      </div>
    </div>
  );
}
